import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { functions } from '../../../../firebase';
import { toast } from 'sonner';
import {
  isDateWithinInclusiveRange,
  normalizeHistoryDateRanges,
  type InclusiveDateRange,
} from 'general/usage';
import { parseFlowVolumeThousands } from 'general/flowVolume';

// Types
export interface ChartData {
  timestamp: string;
  level: number;
  formattedTime: string;
  rawTimestamp: Date;
  deviceId: string;
}
export interface ChartDataTemp {
  timestamp: string;
  temp: number;
  formattedTime: string;
  rawTimestamp: Date;
  deviceId: string;
}

export interface ChartDataLiter {
  timestamp: string;
  liter: number;
  formattedTime: string;
  rawTimestamp: Date;
  deviceId: string;
}

export interface Log {
  Te: number | null;
  EE: number | null;
  l: number | null;
  m2: number | null;
  m3?: number | null;
  cm: number | null;
  log_count: number;
  createTime: Date;
  id: string;
  // Additional raw fields for schema table
  vo?: string | null;
  pu?: string | null;
  al?: string | null;
  v1?: string | null;
  v2?: string | null;
  ex?: string | null;
  on?: string | null;
  [key: string]: any;
}

export interface ProcessedLevelData {
  levelData: ChartData[];
  tempData: ChartDataTemp[];
  literData: ChartDataLiter[];
  weekSummaryLevelData: ChartData[];
  maxLevel: number;
  maxLiter: number;
  latestLog?: ChartData;
  rawLogs: Log[]; // Raw logs for schema table
  pumpRunPeriods: PumpRunPeriod[]; // Pump run periods from backend
  deviceActivity: DeviceActivityEvent[]; // Device activity events from backend
}

// Pump daily usage from backend BigQuery calculation
export interface PumpDailyUsage {
  day: string;
  pump_total_minutes: number;
  daily_usage_minutes: number | null;
}

// Pump run periods from backend BigQuery calculation
export interface PumpRunPeriod {
  start_time: string;
  end_time: string | null;
  duration_minutes: number | null;
  day: string;
}

// Device activity events from backend BigQuery calculation
export interface DeviceActivityEvent {
  field: 'v1' | 'v2' | 'ex' | 'on';
  start_time: string;
  end_time: string;
  count_increase: number;
  day: string;
}

// API function - returns logs, pumpRunPeriods, and deviceActivity from parallel BigQuery queries
const getLogs = async (
  deviceId: string,
  dateRange: Pick<DateRange, 'from' | 'to'> | undefined
): Promise<{
  logs: Log[];
  pumpRunPeriods: PumpRunPeriod[];
  deviceActivity: DeviceActivityEvent[];
}> => {
  if (!deviceId) {
    throw new Error('Device ID is required');
  }

  if (!dateRange?.from || !dateRange?.to) {
    return { logs: [], pumpRunPeriods: [], deviceActivity: [] };
  }

  const functionRef = functions.httpsCallable('getPlantModuleLogs');

  const response: any = await functionRef({
    deviceId,
    start: format(dateRange.from, 'yyyy-MM-dd'),
    end: format(dateRange.to, 'yyyy-MM-dd'),
  });

  const errorCode = response?.data?.httpErrorCode?.status;
  if (errorCode) {
    throw new Error('Failed to fetch sensor data');
  }

  // Map from getPlantModuleLogs raw format
  const logs = (response.data.logs || []).map((d: any) => ({
    cm: d.cm ?? null,
    EE: d.EE ?? null,
    Te: d.Te ?? null,
    l: d.l ?? null,
    m2: parseFlowVolumeThousands(d),
    log_count: 1,
    createTime: new Date(d.event_ts?.value || d.event_ts),
    id: d.id ?? '',
    // Additional raw fields for schema table
    vo: d.vo ?? null,
    pu: d.pu ?? null,
    al: d.al ?? null,
    v1: d.v1 ?? null,
    v2: d.v2 ?? null,
    ex: d.ex ?? null,
    on: d.op ?? null, // op is casted to on
  }));

  const pumpRunPeriods: PumpRunPeriod[] = response.data.pumpRunPeriods || [];
  const deviceActivity: DeviceActivityEvent[] = response.data.deviceActivity || [];

  return { logs, pumpRunPeriods, deviceActivity };
};

function filterSeriesByRange<T extends { rawTimestamp: Date }>(
  series: T[],
  range: InclusiveDateRange | null
): T[] {
  if (!range) {
    return series;
  }

  return series.filter((item) => isDateWithinInclusiveRange(item.rawTimestamp, range));
}

function overlapsInclusiveRange(
  startValue: Date,
  endValue: Date,
  range: InclusiveDateRange | null
): boolean {
  if (!range) {
    return true;
  }

  return startValue.getTime() <= range.to.getTime() && endValue.getTime() >= range.from.getTime();
}

// Process raw logs into chart data
const processLevelData = (
  logs: Log[],
  deviceId: string,
  pumpRunPeriods: PumpRunPeriod[],
  deviceActivity: DeviceActivityEvent[],
  selectedRange: InclusiveDateRange | null
): ProcessedLevelData => {
  const allLevelData: ChartData[] = [];
  const allLiterData: ChartDataLiter[] = [];
  const allTempData: ChartDataTemp[] = [];

  logs.forEach((flowData) => {
    const level = flowData.cm ?? 0;
    const temp = flowData.Te ?? 0;
    const liter = (flowData.l ?? 0) + (flowData.m2 ?? 0) * 1000;

    const timestamp = flowData.createTime;
    const formattedTime = `${timestamp.toLocaleDateString()} ${timestamp.toLocaleTimeString()}`;

    allLevelData.push({
      timestamp: timestamp.toISOString(),
      level,
      formattedTime,
      rawTimestamp: timestamp,
      deviceId,
    });

    allLiterData.push({
      timestamp: timestamp.toISOString(),
      liter,
      formattedTime,
      rawTimestamp: timestamp,
      deviceId,
    });

    allTempData.push({
      timestamp: timestamp.toISOString(),
      temp,
      formattedTime,
      rawTimestamp: timestamp,
      deviceId,
    });
  });

  // Sort by timestamp
  allLevelData.sort((a, b) => a.rawTimestamp.getTime() - b.rawTimestamp.getTime());
  allLiterData.sort((a, b) => a.rawTimestamp.getTime() - b.rawTimestamp.getTime());
  allTempData.sort((a, b) => a.rawTimestamp.getTime() - b.rawTimestamp.getTime());

  const levelData = filterSeriesByRange(allLevelData, selectedRange);
  const literData = filterSeriesByRange(allLiterData, selectedRange);
  const tempData = filterSeriesByRange(allTempData, selectedRange);

  const maxLevel = levelData.reduce((currentMax, item) => Math.max(currentMax, item.level), 0);
  const maxLiter = literData.reduce((currentMax, item) => Math.max(currentMax, item.liter), 0);

  const filteredRawLogs = logs
    .filter((log) => isDateWithinInclusiveRange(log.createTime, selectedRange))
    .sort((a, b) => b.createTime.getTime() - a.createTime.getTime());

  const filteredPumpRunPeriods = pumpRunPeriods.filter((period) => {
    const start = new Date(period.start_time);
    const end = period.end_time ? new Date(period.end_time) : start;
    return overlapsInclusiveRange(start, end, selectedRange);
  });

  const filteredDeviceActivity = deviceActivity.filter((event) =>
    overlapsInclusiveRange(new Date(event.start_time), new Date(event.end_time), selectedRange)
  );

  return {
    levelData,
    literData,
    tempData,
    weekSummaryLevelData: allLevelData,
    maxLevel: Math.max(maxLevel, 100), // Ensure minimum of 100
    maxLiter: Math.max(maxLiter, 100), // Ensure minimum of 100
    latestLog: levelData[levelData.length - 1],
    rawLogs: filteredRawLogs,
    pumpRunPeriods: filteredPumpRunPeriods,
    deviceActivity: filteredDeviceActivity,
  };
};

// Custom hook
export const usePlantModuleSensorData = (deviceId: string, dateRange: DateRange | undefined) => {
  const ranges = React.useMemo(
    () => normalizeHistoryDateRanges(dateRange),
    [dateRange?.from?.getTime(), dateRange?.to?.getTime()]
  );

  const query = useQuery({
    queryKey: [
      'plantModuleSensorData',
      deviceId,
      ranges.summaryFetchRange?.from?.toISOString(),
      ranges.summaryFetchRange?.to?.toISOString(),
    ],
    queryFn: async (): Promise<ProcessedLevelData> => {
      const { logs, pumpRunPeriods, deviceActivity } = await getLogs(
        deviceId,
        ranges.summaryFetchRange ?? undefined
      );
      return processLevelData(logs, deviceId, pumpRunPeriods, deviceActivity, ranges.selectedRange);
    },
    enabled: !!deviceId && !!ranges.summaryFetchRange?.from && !!ranges.summaryFetchRange?.to,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Handle success/error notifications using useEffect
  React.useEffect(() => {
    if (query.isSuccess && ranges.selectedRange?.from && ranges.selectedRange?.to) {
      toast.success(
        `Data blev hentet for id:${deviceId} fra ${format(
          ranges.selectedRange.from,
          'yyyy-MM-dd'
        )} til ${format(ranges.selectedRange.to, 'yyyy-MM-dd')}`
      );
    }
    if (query.isError) {
      console.error('Error fetching level sensor data:', query.error);
      toast.error('Der skete en fejl ved hentning af data');
    }
  }, [query.isSuccess, query.isError, query.error, deviceId, ranges.selectedRange]);

  return query;
};

// Hook for getting current level (latest reading)
export const useCurrentLevel = (deviceId: string, dateRange: DateRange | undefined) => {
  const { data, isLoading, error } = usePlantModuleSensorData(deviceId, dateRange);

  return {
    currentLevel: data?.latestLog?.level || 0,
    currentLevelCm: data?.latestLog?.level || 0,
    isLoading,
    error,
    lastUpdated: data?.latestLog?.rawTimestamp,
  };
};
